export function initSubNav({ nav, desktopBreakpoint = 768 }) {
  const subNav = document.getElementById('sub-nav');
  const subNavToggle = subNav?.querySelector('.sub-nav-toggle');
  const subNavLinks = Array.from(subNav?.querySelectorAll('.sub-nav-link') || []);
  const fycSection = document.querySelector('.for-your-consideration');
  const fycTop = fycSection?.querySelector('.top');
  const fycContentRows = Array.from(document.querySelectorAll('.for-your-consideration .content'));
  const EXTRA_SCROLL_OFFSET = 20;

  const getScrollOffset = () => {
    const navHeight = nav?.getBoundingClientRect().height || nav?.offsetHeight || 0;
    const collapsedSubNavHeight = subNavToggle?.getBoundingClientRect().height || subNavToggle?.offsetHeight || 0;

    return navHeight + collapsedSubNavHeight + EXTRA_SCROLL_OFFSET;
  };

  const scrollToHashWithOffset = (hash) => {
    if (!hash) {
      return;
    }

    const target = document.querySelector(hash);

    if (!target) {
      return;
    }

    const targetTop = window.scrollY + target.getBoundingClientRect().top;
    const offsetTop = Math.max(0, targetTop - getScrollOffset());

    window.scrollTo({
      top: offsetTop,
      behavior: 'smooth',
    });

    if (window.history?.pushState) {
      window.history.pushState(null, '', hash);
    } else {
      window.location.hash = hash;
    }
  };

  const closeTray = () => {
    if (!subNav || !subNavToggle) {
      return;
    }

    subNav.classList.remove('is-open');
    subNavToggle.setAttribute('aria-expanded', 'false');
  };

  const openTray = () => {
    if (!subNav || !subNavToggle) {
      return;
    }

    subNav.classList.add('is-open');
    subNavToggle.setAttribute('aria-expanded', 'true');
  };

  const updateTrayMaxHeight = () => {
    if (!subNav || !subNavToggle) {
      return;
    }

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const isDesktop = window.innerWidth >= desktopBreakpoint;
    const subNavTop = subNav.getBoundingClientRect().top;
    const toggleHeight = subNavToggle.getBoundingClientRect().height || subNavToggle.offsetHeight || 0;
    const availableHeight = isDesktop
      ? Math.floor(viewportHeight - 24)
      : Math.floor(viewportHeight - subNavTop - toggleHeight - 8);
    const trayMaxHeight = Math.max(120, availableHeight);

    subNav.style.setProperty('--sub-nav-tray-max-height', `${trayMaxHeight}px`);
  };

  const updateDesktopPlacement = () => {
    if (!subNav || fycContentRows.length === 0) {
      return;
    }

    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const firstContentRow = fycContentRows[0];
    const lastContentRow = fycContentRows[fycContentRows.length - 1];
    const fycInner = document.querySelector('.for-your-consideration .inner');
    const firstContentRect = firstContentRow.getBoundingClientRect();
    const firstTopViewport = firstContentRect.top;
    const lastTopViewport = lastContentRow.getBoundingClientRect().top;
    const subNavRect = subNav.getBoundingClientRect();
    const subNavHeight = subNavRect.height || subNav.offsetHeight || 0;
    const subNavWidth = subNavRect.width || subNav.offsetWidth || 0;
    const centerTopViewport = (viewportHeight - subNavHeight) / 2;
    const clampedTop = Math.max(firstTopViewport, Math.min(centerTopViewport, lastTopViewport));

    const contentRight = fycInner?.getBoundingClientRect().right || firstContentRect.right;
    const halfWidth = subNavWidth / 2;
    const edgePadding = 12;

    const gutterLeft = contentRight;
    const gutterRight = viewportWidth;
    const gutterCenter = gutterLeft + ((gutterRight - gutterLeft) / 2);
    const minCenter = gutterLeft + halfWidth + edgePadding;
    const maxCenter = gutterRight - halfWidth - edgePadding;
    const clampedLeft = maxCenter >= minCenter
      ? Math.max(minCenter, Math.min(gutterCenter, maxCenter))
      : minCenter;

    subNav.style.setProperty('--sub-nav-desktop-top', `${Math.round(clampedTop)}px`);
    subNav.style.setProperty('--sub-nav-desktop-left', `${Math.round(clampedLeft)}px`);
  };

  const setVisible = (isVisible) => {
    if (!subNav) {
      return;
    }

    subNav.classList.toggle('is-visible', isVisible);
    subNav.setAttribute('aria-hidden', String(!isVisible));

    if (!isVisible) {
      closeTray();
    }
  };

  const updateVisibility = () => {
    if (!subNav || !fycSection || !fycTop || !nav) {
      return;
    }

    const isMobile = window.innerWidth < desktopBreakpoint;

    if (!isMobile) {
      const isVisible = fycContentRows.length > 0;
      subNav.classList.add('is-desktop');
      setVisible(isVisible);

      if (!isVisible) {
        return;
      }

      closeTray();
      updateDesktopPlacement();
      updateTrayMaxHeight();
      return;
    }

    subNav.classList.remove('is-desktop');
    subNav.style.removeProperty('--sub-nav-desktop-top');
    subNav.style.removeProperty('--sub-nav-desktop-left');

    const navHeight = nav.offsetHeight || 83;
    const fycSectionBounds = fycSection.getBoundingClientRect();
    const fycTopBounds = fycTop.getBoundingClientRect();

    const isWithinFycSection = fycSectionBounds.top <= navHeight && fycSectionBounds.bottom > navHeight;
    const isPastFycTop = fycTopBounds.bottom <= navHeight;

    const isVisible = isWithinFycSection && isPastFycTop;
    setVisible(isVisible);

    if (isVisible) {
      updateTrayMaxHeight();
    }
  };

  if (subNav && subNavToggle && fycSection && fycTop) {
    subNavToggle.addEventListener('click', () => {
      if (subNav.classList.contains('is-desktop')) {
        return;
      }

      if (!subNav.classList.contains('is-visible')) {
        return;
      }

      updateTrayMaxHeight();

      const isOpen = !subNav.classList.contains('is-open');
      subNav.classList.toggle('is-open', isOpen);
      subNavToggle.setAttribute('aria-expanded', String(isOpen));
    });

    subNavLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        const targetUrl = new URL(link.href, window.location.href);
        const isSamePagePath = targetUrl.pathname === window.location.pathname;
        const isSamePageSearch = targetUrl.search === window.location.search;

        if (targetUrl.hash && isSamePagePath && isSamePageSearch) {
          event.preventDefault();
          scrollToHashWithOffset(targetUrl.hash);
        }

        closeTray();
      });
    });

    updateVisibility();
  } else {
    setVisible(false);
  }

  return {
    closeTray,
    updateVisibility,
  };
}
